import React, { useEffect, useState, useRef } from "react";
import { BiCheck, BiPlus, BiTrash, BiChevronDown, BiChevronUp } from "react-icons/bi";
import API from "../../../utils/API";
import Loading from "../../../utils/components/Loading";
import toastr from "toastr";
import { useOutletContext } from "react-router";
import AddNewFormDependencyDetail from "../../FormTemplates/components/FormDependency/AddNewFormDependencyDetail";
import timeStampToDateFormat from "../../../utils/helpers/timestampToDateFormat";
import { processLocations, getLocationLabel } from "../../../utils/helpers/processLocationLabels";

const DependencySet = ({ 
  set, 
  handleSetName, 
  handleDelete, 
  refreshFormDependencyDetails,
}) => {
  const [newSetName, setNewSetName] = useState(set?.name);
  const [isEditing, setIsEditing] = useState(false);
  const [translate] = useOutletContext();
  const [modal, setModal] = useState(false);
  const formTemplatesRef = useRef(null);
  const [nextHoldPointNumber, setNextHoldPointNumber] = useState((set?.details?.length || 0) + 1);
  const [previousFormDependencyDetailId, setPreviousFormDependencyDetailId] = useState(null);
  const [isExcepLocExpanded, setIsExcepLocExpanded] = useState(false);
  const [locationLabels, setLocationLabels] = useState([]);
  const [shortExceptionLocations, setShortExceptionLocations] = useState(set.exception_locations.slice(0, 3) || []);

  const handleUpdateName = async () => {
    if (newSetName.length > 0 && newSetName !== set?.name) {
      try {
        await API.putData(`form_dependency/${set.id}`, { 
          name: newSetName 
        });

        toastr.success(translate("project_settings.form_dependencies.form_dependency_set_updated_successfully"));
      } catch (error) {
        toastr.error(translate("project_settings.form_dependencies.form_dependency_set_update_failed"));
        console.error("error:", error);
      }
    }
    setIsEditing(false);
  };

  const handleAddDetail = () => {
    setModal(true);
  };

  const handleAddDetailClick = () => {
    handleAddDetail(set.id);
  };

  const getExceptionLocationsWithParents = async (exceptionLocations) => {
    let ids = exceptionLocations.map((location) => location.id);

    try {
      const idInParams = ids.map(id => `id_in=${id}`).join('&');
      const { data } = await API.getData(`site_set/${set.site.id}/details?${idInParams}`);

      const labels = processLocations(data?.data || []);

      setLocationLabels(labels);
    } catch (error) {
      console.error("error:", error);
    }
  }

  // Check for overflow and apply overflow-start class
  useEffect(() => {
    if (set?.details?.length > 0) {
      setNextHoldPointNumber((set?.details?.length || 0) + 1);
    }

    const checkOverflow = () => {
      if (formTemplatesRef.current) {
        const container = formTemplatesRef.current;
        const hasOverflow = container.scrollWidth > container.clientWidth;
        
        if (hasOverflow) {
          container.classList.add('overflow-start');
        } else {
          container.classList.remove('overflow-start');
        }
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [set?.details]);

  return (
    <>
      <div className="TitleContainer">
        <div className="title-box">
          <input 
            id={`${set.id}-name`} 
            type="text" 
            className="title" 
            value={newSetName} 
            readOnly={!isEditing}
            onDoubleClick={(e) => {
              e.preventDefault();
              setIsEditing(true);
            }}
            onChange={(e) => setNewSetName(e.target.value)}
          />
          {isEditing && (
            <span
              className="apply-button"
              onClick={handleUpdateName}
              role="button"
            >
              <BiCheck size="20px" />
            </span>
          )}
          <span className="delete-button" onClick={() => handleDelete(set.id)} role="button">
            <BiTrash size="20px" />
          </span>
        </div>
        <div className="SiteInfoContainer">
          {set.site && (
            <div className="site-name">
              <span className="label">{translate("project_settings.form_dependencies.site")}:</span>
              <span className="value-chip">{set.site.name}</span>
            </div>
          )}
        </div>
      </div>
      <div className="DetailsContainer">
        {set?.details?.length > 0 && (
          set?.details?.map((detail, index) => (
            <div id={`${set.id}-detail-${index}`} className="DetailsRow" key={detail.id}>
              <div className="FormTemplates" ref={formTemplatesRef}>
                {
                  detail?.templates?.length > 0 ? (
                    detail?.templates?.map((template) => (
                      <div className="FormTemplateItem" key={template.id}>
                        <div className="template-name">
                          <span className="name">{template.name}</span>
                        </div>
                        {template.status_detail_name && (
                          <div className="template-status">
                            <span className="label">{template.status_set_name}:</span>
                            <span className="status">{template.status_detail_name}</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <span className="label">No templates here</span>
                  )
                }
              </div>
              { index < ((set?.details?.length || 0) - 1) ? (
                <div className="HoldPointName">
                  <span className="name">{detail.name}</span>
                </div>
              ) : null }
            </div>
          ))
        )}
        <div className="AddNewHoldPoint" onClick={() => {
          setPreviousFormDependencyDetailId(set?.details.length > 0 ? set?.details[set?.details.length - 1].id : null);
          handleAddDetailClick();
        }}>
          <BiPlus size="20px" />
        </div>
      </div>
      {set.exception_locations && set.exception_locations.length > 0 && (
        <div className={`ExcepLocContainer ${isExcepLocExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="excep-loc-header" onClick={async () => {
            setIsExcepLocExpanded(!isExcepLocExpanded);
            if (!isExcepLocExpanded) {
              await getExceptionLocationsWithParents(set.exception_locations);
            }
          }}>
            <div className="header-content">
              <span className="label">{translate("project_settings.form_dependencies.exception_locations_short")} :</span>
              {!isExcepLocExpanded && (
                <div className="preview-chips">
                  {shortExceptionLocations.map((location, index) => {
                    return (
                      <span key={index} className="excep-loc-chip">
                        {location.name}
                      </span>
                    );
                  })}
                  {set.exception_locations.length > 2 && (
                    <span className="excep-loc-chip more-count">
                      +{set.exception_locations.length - 2}
                    </span>
                  )}
                </div>
              )}
            </div>
            {isExcepLocExpanded ? <BiChevronUp size="18px" /> : <BiChevronDown size="18px" />}
          </div>
          {isExcepLocExpanded && (
            <div className="excep-loc-locations">
              <div className="excep-loc-table-container">
                <table className="excep-loc-table">
                  <tbody>
                    {set.exception_locations.map((location, index) => {
                      const label = getLocationLabel(location.id, locationLabels) || location.name;
                      return (
                        <tr key={index}>
                          <td>{label}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="FooterContainer">
        <div className="footer-info">
          {set.created_at && (
            <div className="info-item">
              <span className="info-label">{translate("project_settings.form_dependencies.created_at")} :</span>
              <span className="info-value">
                    {timeStampToDateFormat(set.created_at, "dd/MM/yyyy HH:mm") || new Date(set.created_at).toLocaleString()}
              </span>
            </div>
          )}
          {set.modified_at && (
            <div className="info-item">
              <span className="info-label">{translate("project_settings.form_dependencies.modified_at")} :</span>
              <span className="info-value">
                {(() => {
                  try {
                    const date = new Date(set.modified_at);
                    if (isNaN(date.getTime())) {
                      return set.modified_at;
                    }
                    return timeStampToDateFormat(date.getTime(), "dd/MM/yyyy HH:mm") || date.toLocaleString();
                  } catch (e) {
                    return set.modified_at;
                  }
                })()}
              </span>
            </div>
          )}
        </div>
      </div>

      <AddNewFormDependencyDetail
        dependencySet={set}
        show={modal}
        setShow={setModal}
        translate={translate}
        refreshFormDependencyDetails={refreshFormDependencyDetails}
        nextHoldPointNumber={nextHoldPointNumber}
        previousFormDependencyDetailId={previousFormDependencyDetailId}
      />
    </>
  );
};
  
export default function DependencySets ({ 
  sets,
  setsLoading,
  handleDelete,
  refreshFormDependencyDetails
}) {

  return (
    <div className="DependencySets">
      {setsLoading && <Loading/>}
      {sets.map((set) => (
          <div className="DependencySet" key={set.id}>
            <DependencySet set={set} handleDelete={handleDelete} refreshFormDependencyDetails={refreshFormDependencyDetails} />
          </div>
      ))}
    </div>
  );
};